/**
 * Composable для работы с cookies быстрой доставки
 */

import { cookies } from 'globalState/elements/cookies.js';
import { DELIVERY_COOKIES, COOKIE_EXPIRY } from '../../constants/delivery.ts';
import type {
    IFastDeliveryState,
    IAddressUpdateParams
} from '../../types/delivery.ts';

export function useFastDeliveryCookie() {
    /**
     * Получить сохраненное состояние доставки из cookies
     * @returns Состояние доставки или null, если cookies не существует
     */
    const getDeliveryState = (): IFastDeliveryState | null => {
        const cookieValue = cookies.getCookie(DELIVERY_COOKIES.DELIVERY_STATE);

        if (!cookieValue) {
            return null;
        }

        try {
            return JSON.parse(cookieValue) as IFastDeliveryState;
        } catch (error) {
            console.error('Ошибка при парсинге productDeliveryState cookie:', error);
            return null;
        }
    };

    /**
     * Сохранить состояние доставки в cookies
     * @param state - Состояние доставки для сохранения
     */
    const setDeliveryState = (state: IFastDeliveryState): void => {
        try {
            cookies.setCookie(
                DELIVERY_COOKIES.DELIVERY_STATE,
                JSON.stringify(state),
                COOKIE_EXPIRY.DELIVERY_STATE
            );

            // Обновляем timestamp последней проверки
            updateLastCheckedTimestamp();
        } catch (error) {
            console.error('Ошибка при сохранении productDeliveryState cookie:', error);
        }
    };

    /**
     * Обновить номер квартиры в существующем состоянии
     * @param appart - Номер квартиры
     * @returns true если обновление успешно, false если cookies не существует
     */
    const updateApartment = (appart: string): boolean => {
        const currentState = getDeliveryState();

        if (!currentState) {
            return false;
        }

        const updatedState: IFastDeliveryState = {
            ...currentState,
            address: {
                ...currentState.address,
                appart,
            },
        };

        setDeliveryState(updatedState);
        return true;
    };

    /**
     * Обновить адрес в существующем состоянии
     * @param addressUpdates - Объект с полями адреса для обновления
     * @returns true если обновление успешно, false если cookies не существует
     */
    const updateAddress = (addressUpdates: IAddressUpdateParams): boolean => {
        const currentState = getDeliveryState();

        if (!currentState) {
            return false;
        }

        const updatedState: IFastDeliveryState = {
            ...currentState,
            address: {
                ...currentState.address,
                ...addressUpdates,
            },
        };

        setDeliveryState(updatedState);
        return true;
    };

    /**
     * Обновить только availability статус
     * @param availableFastDelivery - Доступна ли быстрая доставка
     */
    const updateAvailability = (availableFastDelivery: boolean): boolean => {
        const currentState = getDeliveryState();

        if (!currentState) {
            return false;
        }

        const updatedState: IFastDeliveryState = {
            ...currentState,
            availableFastDelivery,
        };

        setDeliveryState(updatedState);
        return true;
    };

    /**
     * Обновить ближайший магазин
     * @param nearestStore - Название ближайшего магазина
     */
    const updateNearestStore = (nearestStore: string): boolean => {
        const currentState = getDeliveryState();

        if (!currentState) {
            return false;
        }

        const updatedState: IFastDeliveryState = {
            ...currentState,
            nearestStore,
        };

        setDeliveryState(updatedState);
        return true;
    };

    /**
     * Обновить timestamp последней проверки доставки
     */
    const updateLastCheckedTimestamp = (): void => {
        cookies.setCookie(
            DELIVERY_COOKIES.LAST_CHECKED,
            Date.now().toString(),
            COOKIE_EXPIRY.LAST_CHECKED
        );
    };

    /**
     * Получить timestamp последней проверки доставки
     * @returns Timestamp в миллисекундах или null
     */
    const getLastCheckedTimestamp = (): number | null => {
        const cookieValue = cookies.getCookie(DELIVERY_COOKIES.LAST_CHECKED);

        if (!cookieValue) {
            return null;
        }

        const timestamp = parseInt(cookieValue, 10);
        return isNaN(timestamp) ? null : timestamp;
    };

    /**
     * Очистить все cookies доставки
     */
    const clearDeliveryState = (): void => {
        cookies.deleteCookie(DELIVERY_COOKIES.DELIVERY_STATE);
        cookies.deleteCookie(DELIVERY_COOKIES.LAST_CHECKED);
    };

    /**
     * Проверить, нужно ли обновить данные доставки
     * @param maxAgeMinutes - Максимальный возраст данных в минутах (по умолчанию 60)
     * @returns true если данные устарели или отсутствуют
     */
    const isDeliveryStateStale = (maxAgeMinutes: number = 60): boolean => {
        const lastChecked = getLastCheckedTimestamp();

        if (!lastChecked) {
            return true;
        }

        const ageInMinutes = (Date.now() - lastChecked) / (1000 * 60);
        return ageInMinutes > maxAgeMinutes;
    };

    return {
        // Основные методы
        getDeliveryState,
        setDeliveryState,
        clearDeliveryState,

        // Обновление отдельных полей
        updateApartment,
        updateAddress,
        updateAvailability,
        updateNearestStore,

        // Работа с timestamp
        updateLastCheckedTimestamp,
        getLastCheckedTimestamp,
        isDeliveryStateStale,
    };
}

